import eagerComputed from '@nicolaudie-group/ng-ui-kit/helpers/eagerComputed'

import config from '@/config'
import * as find from '@/helpers/profile/find'
import * as parse from '@/helpers/profile/parse'
import routeNames from '@/router/names'
import useRouterStore from '@/stores/router.store'
import { computed, markRaw } from 'vue'

function useProfileParsing(data) {
  const profileUid = eagerComputed(() => {
    const routerStore = useRouterStore()
    return routerStore.route.params.profileUid
  })

  const modeUid = eagerComputed(() => {
    const routerStore = useRouterStore()
    return routerStore.route.params.modeUid || routerStore.route.query.modeUid
  })

  const channelUid = eagerComputed(() => {
    const routerStore = useRouterStore()
    return routerStore.route.params.channelUid || routerStore.route.query.channelUid
  })

  const presetRangeUid = eagerComputed(() => {
    const routerStore = useRouterStore()
    return routerStore.route.params.presetRangeUid || routerStore.route.query.presetRangeUid
  })

  const presetUid = eagerComputed(() => {
    const routerStore = useRouterStore()
    return routerStore.route.params.presetUid || routerStore.route.query.presetUid
  })

  const isGlobal = eagerComputed(() => typeof modeUid.value === 'undefined')

  const report = computed(() => {
    // console.log('Compute "report"')
    const profile = data.value
    const result = parse.report(profile)
    return result ? markRaw(result) : result
  })

  const family = computed(() => {
    const profile = data.value
    let result
    if (profile) {
      result = config.properties.modelsFamilies.find((modelFamily) => (
        modelFamily.value === profile.properties.fix_family
      ))
    }
    return markRaw(result) || null
  })

  const type = computed(() => {
    const profile = data.value
    const familyValue = family.value
    let result
    if (profile) {
      result = familyValue.types.find((modelType) => (
        modelType.value === profile.properties.fix_type
      ))
    }
    return markRaw(result) || null
  })

  const model = computed(() => {
    const typeValue = type.value
    const profile = data.value
    let result
    if (typeValue) {
      // Find the type
      result = typeValue.models.find((modelItem) => (
        modelItem.value === profile.properties.file_object_3d_2
      ))
    }
    return result || null
  })

  const modes = computed(() => {
    // console.log('Compute "modes"')
    const profile = data.value
    const result = parse.modes(profile, report.value)
    return result ? markRaw(result) : result
  })

  /**
   * Find a mode in the provided list of modes.
   *
   * You can provide a custom list if needed
   */
  function findMode(uid, list) {
    const result = find.parsedItem(list || modes.value, uid)
    return result ? markRaw(result) : result
  }

  const mode = computed(() => {
    // console.log('Compute "mode"')
    const result = findMode(modeUid.value)
    // Return the actual mode only when it or one of it's children is selected
    return !isGlobal.value ? result : null
  })

  const localChannels = computed(() => {
    // console.log('Compute "localChannels"')
    const profile = data.value
    const parent = mode.value
    let result = null
    if (!isGlobal.value) {
      // When a mode or one of it's children is selected
      result = parse.localChannels(profile, parent)
    }
    return result ? markRaw(result) : result
  })

  const globalChannels = computed(() => {
    // console.log('Compute "globalChannels"')
    const profile = data.value
    const result = parse.globalChannels(profile, report.value)
    return result ? markRaw(result) : result
  })

  const localChannel = computed(() => {
    // console.log('Compute "localChannel"')
    const list = localChannels.value
    const uid = channelUid.value
    let result = null
    if (!isGlobal.value) {
      // When a mode or one of it's children is selected
      result = find.parsedItem(list, uid)
    }
    return result ? markRaw(result) : result
  })

  const globalChannel = computed(() => {
    // console.log('Compute "globalChannel"')
    const profile = data.value
    const list = globalChannels.value
    const local = localChannel.value
    const uid = channelUid.value
    let result = null
    if (isGlobal.value) {
      // When a global channel or one of it's children is selected
      result = find.parsedItem(list, uid)
    } else if (local && !local.data.lsb) {
      // When a mode or one of it's children is selected,
      // and the channel is not a LSB channel
      // Populate the global equivalent
      result = parse.globalChannel(profile, local.global, report.value)
    }
    return result ? markRaw(result) : result
  })

  const localPresetRanges = computed(() => {
    // console.log('Compute "localPresetRanges"')
    const parent = localChannel.value
    let result = null
    if (!isGlobal.value) {
      // When a mode or one of it's children is selected
      result = parse.localPresetRanges(parent)
    }
    return result ? markRaw(result) : result
  })

  const globalPresetRanges = computed(() => {
    // console.log('Compute "globalPresetRanges"')
    const parent = globalChannel.value
    const local = localChannel.value
    let result = null
    if (isGlobal.value) {
      // When a global channel or one of it's children is selected
      result = parse.globalPresetRanges(parent)
    } else if (local && !local.data.lsb) {
      // When a mode or one of it's children is selected,
      // and the channel is not a LSB channel
      // Populate the global equivalent
      result = parse.globalPresetRanges(parent, {
        // Override the link to target the preset ranges in the local channel
        // instead of in the global channel
        name: routeNames.LocalPresetRange,
        params: local.link.params,
      })
    }
    return result ? markRaw(result) : result
  })

  const localPresetRange = computed(() => {
    // console.log('Compute "localPresetRange"')
    const list = localPresetRanges.value
    const uid = presetRangeUid.value
    let result = null
    if (!isGlobal.value) {
      // When a mode or one of it's children is selected
      result = find.parsedItem(list, uid)
    }
    return result ? markRaw(result) : result
  })

  const globalPresetRange = computed(() => {
    // console.log('Compute "globalPresetRange"')
    const list = globalPresetRanges.value
    const local = localChannel.value
    const uid = presetRangeUid.value
    let result = null
    if (isGlobal.value) {
      // When a global channel or one of it's children is selected
      result = find.parsedItem(list, uid)
    } else if (local && !local.data.lsb) {
      // When a mode or one of it's children is selected,
      // and the channel is not a LSB channel
      // Populate the global equivalent
      result = find.parsedItem(list, uid)
    }
    return result ? markRaw(result) : result
  })

  const localPresets = computed(() => {
    // console.log('Compute "localPresets"')
    const parent = localPresetRange.value
    let result = null
    if (!isGlobal.value) {
      // When a mode or one of it's children is selected
      result = parse.localPresets(parent)
    }
    return result ? markRaw(result) : result
  })

  const globalPresets = computed(() => {
    // console.log('Compute "globalPresets"')
    const parent = globalPresetRange.value
    const local = localChannel.value
    let result = null
    if (isGlobal.value) {
      // When a global channel or one of it's children is selected
      result = parse.globalPresets(parent)
    } else if (local && !local.data.lsb) {
      // When a mode or one of it's children is selected,
      // and the channel is not a LSB channel
      // Populate the global equivalent
      result = parse.globalPresets(parent, {
        // Override the link to target the presets in the local channel
        // instead of in the global channel
        name: routeNames.LocalPreset,
        params: local.link.params,
      })
    }
    return result ? markRaw(result) : result
  })

  const localPreset = computed(() => {
    // console.log('Compute "localPreset"')
    const list = localPresets.value
    const uid = presetUid.value
    let result = null
    if (!isGlobal.value) {
      // When a mode or one of it's children is selected
      result = find.parsedItem(list, uid)
    }
    return result ? markRaw(result) : result
  })

  const globalPreset = computed(() => {
    // console.log('Compute "globalPreset"')
    const list = globalPresets.value
    const local = localChannel.value
    const uid = presetUid.value
    let result = null
    if (isGlobal.value) {
      // When a global channel or one of it's children is selected
      result = find.parsedItem(list, uid)
    } else if (local && !local.data.lsb) {
      // When a mode or one of it's children is selected,
      // and the channel is not a LSB channel
      // Populate the global equivalent
      result = find.parsedItem(list, uid)
    }
    return result ? markRaw(result) : result
  })

  const channels = computed(() => {
    // console.log('Compute "channels"')
    const local = localChannels.value
    const global = globalChannels.value
    const result = isGlobal.value ? global : local
    return result ? markRaw(result) : result
  })

  const channel = computed(() => {
    // console.log('Compute "channel"')
    const local = localChannel.value
    const global = globalChannel.value
    let result = null
    if (local || global) {
      // If there is a global channel, set the link and the data
      if (global) {
        result = { ...global }
      } else {
        result = {
          link: {},
          data: {},
        }
      }
      // If there is a local channel
      if (local) {
        result = {
          ...result,
          // Override the link, and add other properties that are on the local channel
          ...local,
          data: {
            // and merge the data
            ...result.data,
            ...local.data,
          },
        }
      }
    }
    return result ? markRaw(result) : result
  })

  const presetRanges = computed(() => {
    // console.log('Compute "presetRanges"')
    const local = localPresetRanges.value
    const global = globalPresetRanges.value
    const result = local || global
    return result ? markRaw(result) : result
  })

  const presetRange = computed(() => {
    // console.log('Compute "presetRange"')
    const local = localPresetRange.value
    const global = globalPresetRange.value
    const result = local || global
    return result ? markRaw(result) : result
  })

  const presets = computed(() => {
    // console.log('Compute "presets"')
    const local = localPresets.value
    const global = globalPresets.value
    const result = local || global
    return result ? markRaw(result) : result
  })

  const preset = computed(() => {
    // console.log('Compute "preset"')
    const local = localPreset.value
    const global = globalPreset.value
    const result = local || global
    return result ? markRaw(result) : result
  })

  const object = computed(() => {
    // console.log('Compute "object"')
    const currentPreset = preset.value
    const currentPresetRange = presetRange.value
    const currentChannel = channel.value
    const currentMode = mode.value
    const result = currentPreset || currentPresetRange || currentChannel || currentMode
    return result ? markRaw(result) : result
  })

  return {
    profileUid,
    modeUid,
    channelUid,
    presetRangeUid,
    presetUid,
    isGlobal,
    report,
    family,
    type,
    model,
    modes,
    findMode,
    mode,
    globalChannels,
    globalChannel,
    localChannels,
    localChannel,
    channels,
    globalPresetRanges,
    globalPresetRange,
    localPresetRanges,
    localPresetRange,
    globalPresets,
    globalPreset,
    localPresets,
    localPreset,
    channel,
    presetRanges,
    presetRange,
    presets,
    preset,
    object,
  }
}

export default useProfileParsing
